import { Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../Button";
import { ColumnFilterWrapper } from "./ColumnFilterWrapper";

const { Option, OptGroup } = Select;

type Props = {
  confirm: () => void;
  close: () => void;
};

type ItemObject = {
  name: string;
  value: string;
  group?: string;
};

type SelectFilterProps<T extends string | number | ItemObject> = {
  title?: string;
  placeholder?: string;
  initialTagCount?: number;
  itemsToDisplay: T[];
  selectedItems: string[];
  handleSelectionChange: (items: string[]) => void;
  props: Props;
  onSearch?: (value: string) => void;
  isOptionsLoading?: boolean;
  maxCount?: number;
  isTags?: boolean;
  before?: React.ReactNode;
  filterCount?: number;
  sortActive?: boolean;
  sortDirection?: "asc" | "desc";
  onSortChange?: (dir?: "asc" | "desc") => void;
  onReset?: () => void;
};

const SelectFilter = <T extends string | number | ItemObject>({
  title,
  placeholder = "Select From List",
  initialTagCount = 3,
  maxCount,
  itemsToDisplay,
  selectedItems,
  handleSelectionChange,
  onSearch,
  isOptionsLoading = false,
  isTags = false,
  props: { confirm, close },
  before,
  filterCount,
  sortActive,
  sortDirection,
  onSortChange,
  onReset,
}: SelectFilterProps<T>) => {
  const [tempItems, setTempItems] = useState<string[]>(selectedItems);

  const cleanPlaceholder = placeholder
    ? placeholder.replace(/^(Select|Choose)\s+/i, "")
    : "";
  const displayTitle =
    title ||
    (cleanPlaceholder
      ? cleanPlaceholder.charAt(0).toUpperCase() + cleanPlaceholder.slice(1)
      : undefined);

  // Sync tempItems when selectedItems change externally
  useEffect(() => {
    setTempItems(selectedItems);
  }, [JSON.stringify(selectedItems)]);

  const isObjectArray =
    itemsToDisplay.length > 0 && typeof itemsToDisplay[0] === "object";
  const processedItems = itemsToDisplay.map((item) =>
    isObjectArray
      ? {
          key: (item as ItemObject).value,
          label: (item as ItemObject).name,
          group: (item as ItemObject).group,
        }
      : { key: String(item), label: String(item), group: undefined }
  );

  // Group items by their group property
  const groupedItems = useMemo(() => {
    const hasGroups = processedItems.some((item) => item.group);
    if (!hasGroups) return null;

    const groups = new Map<string, typeof processedItems>();
    processedItems.forEach((item) => {
      const groupKey = item.group || "Ungrouped";
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(item);
    });

    return Array.from(groups.entries()).map(([groupName, items]) => ({
      groupName,
      items,
    }));
  }, [processedItems]);

  const isMaxcount1 = maxCount === 1;

  const handleApply = () => {
    handleSelectionChange(tempItems);
    confirm();
  };

  const handleResetInternal = () => {
    setTempItems([]);
    handleSelectionChange([]);
    if (onReset) onReset();
    confirm();
  };

  return (
    <ColumnFilterWrapper
      title={displayTitle}
      filterCount={filterCount ?? tempItems.length}
      isFiltered={tempItems.length > 0}
      sortActive={sortActive}
      sortDirection={sortDirection}
      onSortChange={onSortChange}
      confirm={confirm}
      close={close}
      onApply={handleApply}
      onReset={handleResetInternal}
    >
      {before}
      <div style={{ padding: "4px 0" }}>
        {!isMaxcount1 && !isTags && (
          <Button
            type="text"
            size="small"
            style={{ marginBottom: 8, padding: "0 4px", height: "auto", fontSize: 11, fontWeight: 600, color: "#635bff" }}
            onClick={() =>
              setTempItems(
                tempItems.length < itemsToDisplay.length
                  ? processedItems.map((item) => item.key)
                  : []
              )
            }
          >
            {tempItems.length < itemsToDisplay.length
              ? "Select All"
              : "Deselect All"}
          </Button>
        )}

        <Select
          mode={isTags ? "tags" : "multiple"}
          maxTagCount={initialTagCount}
          style={{ width: "100%" }}
          maxCount={maxCount}
          placeholder={placeholder}
          value={tempItems}
          onChange={(values) => setTempItems(values)}
          onSearch={onSearch}
          filterOption={
            onSearch
              ? false
              : (input, option) => {
                  if (!option?.children) return false;
                  return String(option.children)
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }
          }
          loading={isOptionsLoading}
        >
          {groupedItems
            ? groupedItems.map(({ groupName, items }) => (
                <OptGroup key={groupName} label={groupName}>
                  {items.map((item) => (
                    <Option key={item.key} value={item.key}>
                      {item.label}
                    </Option>
                  ))}
                </OptGroup>
              ))
            : processedItems.map((item) => (
                <Option key={item.key} value={item.key}>
                  {item.label}
                </Option>
              ))}
        </Select>
      </div>
    </ColumnFilterWrapper>
  );
};

export default SelectFilter;
